import React, { useState } from 'react';
import { Navigation } from '../App';
import { Calendar, CheckCircle, Phone, Mail, MapPin, Clock } from 'lucide-react';

export default function BookingPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    smsConsent: false
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-primary/10 to-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4">
            Book Your Free Consultation
          </h1>
          <p className="text-xl text-muted-foreground">
            Schedule a call with our poker experts and start your journey to mastery.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-heading font-bold mb-6">Get in Touch</h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Ready to take your poker game to the next level? Fill out the form and we'll get back to you within 24 hours.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Location</div>
                    <div className="text-muted-foreground">New Braunfels, TX 78130</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Email</div>
                    <a href="mailto:info@luckorskills.com" className="text-primary hover:text-primary/80">
                      info@luckorskills.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Phone</div>
                    <a href="tel:+18302372821" className="text-primary hover:text-primary/80">
                      (830) 237-2821
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Hours</div>
                    <div className="text-muted-foreground">
                      Monday - Friday: 9am - 6pm CST<br />
                      Saturday: 10am - 4pm CST
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-bold mb-3">What to Expect</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>15-minute consultation call</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Discuss your poker goals</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Learn about our programs</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Get personalized recommendations</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Booking Form */}
            <div>
              <div className="bg-card border border-border rounded-xl p-8">
                <h3 className="text-2xl font-heading font-bold mb-6">Schedule Your Call</h3>

                {submitted && (
                  <div className="mb-6 p-4 bg-green-600/10 border border-green-600/30 rounded-lg text-green-400 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    <span>Thank you! We'll be in touch soon.</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="w-full bg-background border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="w-full bg-background border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-background border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full bg-background border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  {/* SMS CONSENT CHECKBOX - A2P 10DLC COMPLIANCE */}
                  <div className="bg-secondary/30 border border-border rounded-lg p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="smsConsent"
                        checked={formData.smsConsent}
                        onChange={handleChange}
                        className="mt-1 w-4 h-4 rounded border-border text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-muted-foreground leading-relaxed">
                        I agree to receive text messages from <strong className="text-foreground">Luck or Skills</strong> regarding appointment confirmations, reminders, and service updates. Message frequency may vary. Message and data rates may apply. Reply STOP to opt out or HELP for assistance. View our{' '}
                        <a href="/privacy-policy" className="text-primary hover:text-primary/80 underline">
                          Privacy Policy
                        </a>
                        {' '}and{' '}
                        <a href="/terms-of-service" className="text-primary hover:text-primary/80 underline">
                          Terms of Service
                        </a>
                        .
                      </span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Message (Optional)</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full bg-background border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Tell us about your poker experience and goals..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-primary hover:bg-primary/90 text-black font-bold py-4 rounded-lg transition-all flex items-center justify-center gap-2"
                  >
                    <Calendar className="w-5 h-5" />
                    Book Free Call
                  </button>

                  <p className="text-xs text-center text-muted-foreground">
                    By submitting this form, you agree to our{' '}
                    <a href="/privacy-policy" className="text-primary hover:text-primary/80 underline">
                      Privacy Policy
                    </a>
                    {' '}and{' '}
                    <a href="/terms-of-service" className="text-primary hover:text-primary/80 underline">
                      Terms of Service
                    </a>
                    .
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calendar Widget Section */}
      <section className="py-12 bg-card/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-heading font-bold mb-4">Or Book Directly</h2>
            <p className="text-muted-foreground">
              Choose a time that works for you using our calendar below.
            </p>
          </div>

          {/* GoHighLevel Calendar Widget Placeholder */}
          <div className="bg-card border border-border rounded-xl p-8 min-h-[600px] flex items-center justify-center">
            <div className="text-center">
              <Calendar className="w-16 h-16 text-primary mx-auto mb-4" />
              <p className="text-muted-foreground mb-2">Calendar Widget</p>
              <p className="text-sm text-muted-foreground">
                Insert your GoHighLevel calendar embed code here
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
